/** Shared query keys and lightweight help-client session state. */
import { create } from 'zustand';

/**
 * Canonical query keys. Every help-client hook keys its `useQuery`
 * call here so a single `queryClient.invalidateQueries({queryKey:
 * helpKeys.all})` invalidates every help query in one call.
 */
export const helpKeys = {
  all: ['help'] as const,
  tooltips: () => [...helpKeys.all, 'tooltips'] as const,
  tooltip: (helpKey: string, locale: string) =>
    [...helpKeys.tooltips(), helpKey, locale] as const,
  articles: () => [...helpKeys.all, 'articles'] as const,
  article: (id: string, locale: string) =>
    [...helpKeys.articles(), 'item', id, locale] as const,
  articleList: (params: ArticleListKeyParams) =>
    [...helpKeys.articles(), 'list', normalizeListParams(params)] as const,
  search: (query: string, locale: string, platform: string | null) =>
    [...helpKeys.all, 'search', query, locale, platform ?? '*'] as const,
  releaseNotes: (channel: string | null) =>
    [...helpKeys.all, 'release-notes', channel ?? '*'] as const,
  walkthrough: (id: string, locale: string) =>
    [...helpKeys.all, 'walkthroughs', id, locale] as const,
  /** Phase 35.8a — pending walkthroughs for a (platform, role) tuple.
   *  Drives the runner's auto-start decision; data[0] is the candidate. */
  walkthroughPending: (
    platform: string,
    role: string | null,
    locale: string,
  ) =>
    [
      ...helpKeys.all,
      'walkthroughs',
      'pending',
      platform,
      role ?? '*',
      locale,
    ] as const,
  /** Phase 35.8a — full walkthrough list for the help-drawer "Walkthroughs"
   *  tab. Includes completed walkthroughs by default. */
  walkthroughList: (
    platform: string,
    role: string | null,
    includeCompleted: boolean,
    locale: string,
  ) =>
    [
      ...helpKeys.all,
      'walkthroughs',
      'list',
      platform,
      role ?? '*',
      includeCompleted ? 'with-completed' : 'pending-only',
      locale,
    ] as const,
  /** Phase 35.6a — caller-scoped progress rows, keyed by article id.
   *  Keyed without locale because progress is user-state, not content. */
  progress: (articleId: string) =>
    [...helpKeys.all, 'progress', articleId] as const,
  /** Phase 35.6a — caller's recent-progress list. Keyed by limit so a
   *  consumer rendering "show last 5" + "show last 20" don't collide. */
  recentProgress: (limit: number | null) =>
    [...helpKeys.all, 'progress', 'recent', limit ?? 'default'] as const,
} as const;

/**
 * Shape that's both the input to `helpKeys.articleList` and what
 * `normalizeListParams` projects into the cache key. Keeping it
 * exported so a downstream consumer wiring a custom React Query call
 * can reproduce the cache key precisely (e.g. for SSR-style hydration
 * paths that pre-warm the cache).
 */
export interface ArticleListKeyParams {
  platform?: string | null;
  category?: string | null;
  search?: string | null;
  locale?: string | null;
  cursor?: string | null;
}

/**
 * Pure key normalizer. Two things matter here:
 *   1. Order — React Query treats `[a, b]` and `[b, a]` as different
 *      keys. Sorting the param entries before serializing keeps the
 *      cache hit rate high regardless of how the caller passed args.
 *   2. Empty / undefined — collapsed to a sentinel '*' so an explicit
 *      `category: null` and an omitted `category` produce the same
 *      key. Matches the resolver's "missing = no filter" semantics.
 */
export function normalizeListParams(
  params: ArticleListKeyParams,
): Record<string, string> {
  const out: Record<string, string> = {};
  const ordered: Array<keyof ArticleListKeyParams> = [
    'category',
    'cursor',
    'locale',
    'platform',
    'search',
  ];
  for (const key of ordered) {
    const raw = params[key];
    out[key] = raw == null || raw === '' ? '*' : String(raw);
  }
  return out;
}

/**
 * Per-app session state the help-client SDK tracks across hook calls.
 */
interface HelpClientSessionState {
  /** BCP-47 locale; defaults to `en-US`. Each hook reads this when
   *  the caller doesn't pass an explicit locale opt. */
  locale: string;
  /** When true, every hook returns the in-repo fallback (or null)
   *  without hitting the transport. Lets the storefront FAQ render
   *  pre-auth + lets `pos.offline_mode` callers stay functional. */
  disableNetwork: boolean;
  /** Wall-clock ms when the last `markHelpContentStale()` fired.
   *  Hooks include this in their query key so a 35.2b realtime push
   *  cleanly invalidates without forcing every consumer to call
   *  `queryClient.invalidateQueries`. Initialized to 0. */
  staleAtMs: number;

  setLocale: (locale: string) => void;
  setDisableNetwork: (disabled: boolean) => void;
  markHelpContentStale: () => void;
  reset: () => void;
}

const INITIAL = {
  locale: 'en-US',
  disableNetwork: false,
  staleAtMs: 0,
};

export const useHelpClientStore = create<HelpClientSessionState>((set) => ({
  ...INITIAL,
  setLocale: (locale) =>
    set({ locale: typeof locale === 'string' && locale ? locale : 'en-US' }),
  setDisableNetwork: (disabled) => set({ disableNetwork: Boolean(disabled) }),
  markHelpContentStale: () => set({ staleAtMs: Date.now() }),
  reset: () => set(INITIAL),
}));

/**
 * React-friendly accessor for the active locale. Single subscriber
 * point so a locale change rerenders every help-aware UI surface
 * without per-hook locale plumbing.
 */
export function useHelpLocale(): string {
  return useHelpClientStore((s) => s.locale);
}

/** Non-React accessor (e.g. for service-layer prefetch). */
export function getHelpLocale(): string {
  return useHelpClientStore.getState().locale;
}

/** True when every hook should short-circuit to the fallback path. */
export function getHelpNetworkDisabled(): boolean {
  return useHelpClientStore.getState().disableNetwork;
}

/** ms watermark; included in every hook's query key. */
export function getHelpStaleAtMs(): number {
  return useHelpClientStore.getState().staleAtMs;
}
